import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { getSearchFilters } from '@/utils';
import { searchStatBanks, StatBankSearchResponse } from '@/api/statbank-data';
import { UserContext, SystemFeaturesContext, ModuleBase } from '@/components';
import { ModuleBaseModel, ModuleBasePropsModel } from '@/types';
import StatBankFilters from '@/components/statbank/StatBankFilters';
import StatBankSearchResult from '@/components/statbank/StatBankSearchResult';
import { StatBankFilterType } from '@/components/statbank/StatBankFilters';
import { useTranslations } from 'next-intl';

type StatBankModuleModel = ModuleBaseModel<
  {
    availableFilters: StatBankFilterType[];
    displayFilters: boolean;
    displayViewMoreButton: boolean;
    link?: { href: string };
    numberOfStatsPerPage: number;
    requestFilter: any;
  } & ModuleBasePropsModel
>;

const StatBankModule = (moduleData: StatBankModuleModel) => {
  const { props: data } = moduleData;
  const router = useRouter();
  const user = useContext(UserContext);
  const { statBankPage } = useContext(SystemFeaturesContext);
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<StatBankSearchResponse>({
    facets: [],
    results: [],
    totalResults: 0,
  });

  const t = useTranslations();

  const pageSize = data.numberOfStatsPerPage || 1;

  useEffect(() => {
    getData();
  }, [router.query]);

  const getData = async () => {
    const nameMap: { [key: string]: string } = {};
    data.availableFilters?.forEach((filter: any) => {
      nameMap[filter.key] = filter.name;
    });

    const filters = getSearchFilters(router, nameMap);
    setLoading(true);

    const loadingResults = [];
    for (let index = 0; index < pageSize; index++) {
      loadingResults.push({ loading: true, pageId: index, items: [] });
    }
    setResult({ facets: [], results: loadingResults as any, totalResults: 0 });

    try {
      const response = await searchStatBanks({
        ...data.requestFilter,
        searchTerm: (router.query.searchTerm as string) || '*',
        filters: data.displayFilters ? filters : data.requestFilter?.filters || [],
        pageSize,
        page: Number(router.query.page || 1) - 1,
        account: user?.account,
      });

      setResult(response);
    } catch (error) {
      console.error('Error fetching stat bank data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!statBankPage) return null;

  return (
    <ModuleBase data={moduleData} noPadding>
      {data.displayFilters && <StatBankFilters filters={data.availableFilters} result={result} />}

      {data.displayViewMoreButton && (
        <div className="container pt-3">
          <div className="flex justify-end py-2">
            <a href={data.link?.href} className="btn secondary">
              {t('general.$viewMore')}
            </a>
          </div>
        </div>
      )}

      <StatBankSearchResult data={data} result={result} pageSize={pageSize} loading={loading} />
    </ModuleBase>
  );
};

export default StatBankModule;
